import React from 'react';
import { useState } from 'react';
import { Button, Grid, styled, Tooltip, Typography } from '@mui/material';
import FadeIn from '../../../../../../Animations/FadeIn/FadeIn';
import { CachedRounded } from '@mui/icons-material';
import ExpirationOptionChip from '../ExpirationOptionChip/ExpirationOptionChip';
import ImageSelector from '../ImageSelector';
import { updateGallery } from '../../../../../../../services/galleryService';
import { useAppData } from '../../../../../../../hooks/AppDataContext';

const Root = styled(FadeIn)(({ theme }) => ({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '30px',
	flex: 1
}));

const TitlesContainer = styled(Grid)(({ theme }) => ({
	marginTop: 10,
	marginBottom: 20,
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '10px'
}));

const Title = styled(Typography)(({ theme }) => ({
	fontSize: '1.7rem',
	textAlign: 'center'
}));

const SecondaryTitle = styled(Typography)(({ theme }) => ({
	maxWidth: 450,
	marginBottom: 5,
	fontSize: '0.9rem',
	color: theme.palette.text.tertiary,
	textAlign: 'center',
	fontWeight: 300
}));

const Content = styled(Grid)(({ theme }) => ({
	width: '100%',
	height: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '8vw'
}));

const CoverImageContainer = styled(Grid)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: '25px'
}));

const CoverImageWrapper = styled(Grid)(({ theme }) => ({
	width: '30vw',
	height: '30vw',
	borderRadius: '10px',
	display: 'flex',
	boxShadow: `0 0 20px -10px ${theme.palette.separator.primary}`
}));

const Image = styled('img')(({ theme }) => ({
	width: 'inherit',
	height: 'inherit',
	borderRadius: 'inherit',
	objectFit: 'cover'
}));

const ChangeImageButton = styled(Button)(({ theme }) => ({
	width: 'fit-content',
	padding: '6px',
	minWidth: 0,
	borderRadius: '50%',
	border: `1px solid ${theme.palette.text.primary}`
}));

const ChangeImageIcon = styled(CachedRounded)(({ theme }) => ({
	width: '20px',
	height: '20px'
}));

const GalleryExpirationContainer = styled(Grid)(({ theme }) => ({
	width: '30vw',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '25px'
}));

const ExpirationOptions = styled(Grid)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: '10px'
}));

const ExpirationOptionsRow = styled(Grid)(({ theme }) => ({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	gap: '10px'
}));

const PublishGallery = ({ data, setData }) => {
	const DAY = 24 * 60 * 60 * 1000;

	const { updateGalleryState } = useAppData();
	const [openThumbnailDialog, setOpenThumbnailDialog] = useState(false);

	const setDuration = (v) => {
		const updatedData = { ...data, duration: v };
		setData(updatedData);
		updateGalleryState(updatedData);
		updateGallery(data?._id, { duration: v });
	};

	/**
	 * Sets the thumbnail url to the state
	 * @param {string} thumbnail - An image URL
	 */
	const setThumbnail = async (thumbnail) => {
		const updatedData = { ...data, thumbnail };
		await updateGallery(data?._id, { thumbnail });
		setData(updatedData);
		updateGalleryState(updatedData);
	};

	return (
		<Root>
			<TitlesContainer>
				<Title>Finish & Publish Your Gallery</Title>
			</TitlesContainer>
			<Content>
				<CoverImageContainer>
					<CoverImageWrapper>
						<Image src={data?.thumbnail} />
					</CoverImageWrapper>
					<Tooltip title="Change gallery cover image">
						<ChangeImageButton onClick={() => setOpenThumbnailDialog(true)}>
							<ChangeImageIcon />
						</ChangeImageButton>
					</Tooltip>
				</CoverImageContainer>
				<GalleryExpirationContainer>
					<SecondaryTitle>When would you like the gallery to expire?</SecondaryTitle>
					<ExpirationOptions>
						<ExpirationOptionsRow>
							<ExpirationOptionChip
								label="24 Hours"
								value={DAY}
								active={data?.duration === DAY}
								onClick={setDuration}
							/>
							<ExpirationOptionChip
								label="7 Days"
								value={7 * DAY}
								active={data?.duration === 7 * DAY}
								onClick={setDuration}
							/>
						</ExpirationOptionsRow>
						<ExpirationOptionsRow>
							<ExpirationOptionChip
								label="14 Days"
								value={14 * DAY}
								active={data?.duration === 14 * DAY}
								onClick={setDuration}
							/>
							<ExpirationOptionChip
								label="30 Days"
								value={30 * DAY}
								active={data?.duration === 30 * DAY}
								onClick={setDuration}
							/>
							<ExpirationOptionChip
								label="90 Days"
								active={data?.duration === 90 * DAY}
								value={90 * DAY}
								onClick={setDuration}
							/>
						</ExpirationOptionsRow>
						<ExpirationOptionsRow>
							<ExpirationOptionChip
								label="1 year"
								value={365 * DAY}
								active={data?.duration === 365 * DAY}
								onClick={setDuration}
							/>
							<ExpirationOptionChip
								label="Unlimited"
								value={0 * DAY}
								active={data?.duration === 0 * DAY}
								onClick={setDuration}
							/>
						</ExpirationOptionsRow>
					</ExpirationOptions>
				</GalleryExpirationContainer>
			</Content>
			<ImageSelector
				images={data?.images}
				thumbnail={data?.thumbnail}
				setThumbnail={setThumbnail}
				open={openThumbnailDialog}
				close={() => setOpenThumbnailDialog(false)}
			/>
		</Root>
	);
};

export default PublishGallery;
